import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 3: "Current" — Tier 3
// Introduces Amplifying and Dampening pathways alongside Clear.
// Nodes: 5–7, Pathways: 5–7, Budget: minimum + 1–2
// Uses Unison, P5, M3 harmonic requirements.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ch3-01: "Surge"
 * 5 nodes linear, 5 pathways, 1 strike
 *
 * Layout: A(1)--[Amp,unison]--B(10)--[Clear,unison]--C(10)--[Amp,unison]--D(6)--[Clear,unison]--E(6)
 *
 * Strike A:
 *   B: ring 1, Amplifying → delta = 1*2 = 2. B: 10+2=12
 *   C: ring 2, Clear → delta = 2*1 = 2. C: 10+2=12
 *   D: ring 3, Amplifying → delta = 3*2 = 6. D: 6+6=12
 *   E: ring 4 → no effect. E: 6 (unchanged — but wait, ring 4 doesn't affect)
 *
 * Wait, we need E to also be 12. We only get rings 1-3. Let me redesign.
 *
 * Simpler: 5 nodes star topology, hub + 4 leaves.
 *
 * Layout:       B(10)
 *               |  Amp
 *   C(10)--Clear--A(1)--Clear--D(10)
 *               |  Damp
 *              E(1)
 *
 * Strike A:
 *   B: ring 1, Amp → delta = 1*2 = 2. B: 10+2=12. A-B unison: |1-12|=11 or 1. Need 0. ✗
 *
 * Let me think more carefully. For unison we need all linked to be same freq.
 * Let me use mixed intervals.
 *
 * ch3-01: "Surge"
 * 5 nodes, 5 pathways. Hub + 4 spokes. 1 strike.
 *
 * Layout:       B(5)
 *               | Amp, P5
 *   C(9)--[Clear,M3]--A(1)--[Damp,unison]--D(1)
 *               | Clear, m3
 *              E(3)
 *
 * Strike A:
 *   B: ring1, Amp → delta=1*2=2. B: 5+2=7. A-B: |1-7|=6 or (7-1)=6. P5=7. ✗
 *   Need (B_new - A) mod12 = 7 or 5. B_new = 1+7=8 or 1+5=6. With +2 delta, B must start at 6 or 4.
 *   B starts at 6: B+2=8. (8-1)%12=7. P5 ✓
 *
 * Redesign:
 *   A(1), B(6)[Amp,P5], C(9)[Clear,M3], D(1)[Damp,unison], E(10)[Clear,m3]
 *
 * Strike A:
 *   B: ring1, Amp → delta=2. B:6+2=8. |8-1|=7. P5 ✓
 *   C: ring1, Clear → delta=1. C:9+1=10. |10-1|=9. M3=4. (1-10)%12+12)%12=3. ✗
 *   Nope. Need |A-C| mod12 = 4 for M3. C_new - 1 mod12 = 4 or 8. C_new=5 or 9.
 *   With delta=1 (Clear ring1), C = C_start+1 = 5 means C_start=4. Or C=9 means C_start=8.
 *   Let C start at 4: C+1=5. (5-1)=4. M3 ✓
 *   D: ring1, Damp → delta=floor(1*0.5)=0. D unchanged=1. |1-1|=0. Unison ✓
 *   E: ring1, Clear → delta=1. E:10+1=11. |11-1|=10. (1-11+12)%12=2. m3=3. ✗
 *   E_new needs (E_new-1)%12 = 3 or 9. E_new=4 or 10. With +1, E_start=3 or 9.
 *   Let E start at 3: E+1=4. (4-1)=3. m3 ✓
 *
 * Final: A(1), B(6), C(4), D(1), E(3). All hub spokes.
 * Pathways: A-B Amp P5, A-C Clear M3, A-D Damp unison, A-E Clear m3
 * That's only 4 pathways. Need 5.
 * Add: B-C Clear unison? After strike: B=8, C=5. |8-5|=3. unison=0. ✗
 * Add: B-E Clear m3? B=8, E=4. |8-4|=4. m3=3. ✗
 * Add: C-E Clear unison? C=5, E=4. |5-4|=1. ✗
 * Add: D-E Clear m3? D=1, E=4. |4-1|=3. m3 ✓
 *
 * So: A-B Amp P5, A-C Clear M3, A-D Damp unison, A-E Clear m3, D-E Clear m3
 * That's 5 pathways. ✓
 *
 * Verify initial not solved:
 *   A-B: |1-6|=5, P5=7 ✗
 *   A-C: |1-4|=3, M3=4 ✗
 *   A-D: |1-1|=0 unison ✓ (this one is already satisfied)
 *   A-E: |1-3|=2, m3=3 ✗
 *   D-E: |1-3|=2, m3=3 ✗
 * Not 100%. ✓ (only 1/5 satisfied = 20%)
 *
 * Strike A:
 *   B: ring1, Amp → delta=2. B: 6+2=8. (8-1)%12=7. P5 ✓
 *   C: ring1, Clear → delta=1. C: 4+1=5. (5-1)%12=4. M3 ✓
 *   D: ring1, Damp → delta=floor(1*0.5)=0. D stays 1. unison ✓
 *   E: ring1, Clear → delta=1. E: 3+1=4. (4-1)%12=3. m3 ✓
 *   D-E: D=1, E=4. (4-1)%12=3. m3 ✓
 * All 5 satisfied! ✓
 */
export const ch3_01: PuzzleDefinition = {
  id: 'ch3-01',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Surge',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.5, y: 0.1, frequency: 6, locked: false },
      { id: 'C', x: 0.9, y: 0.5, frequency: 4, locked: false },
      { id: 'D', x: 0.1, y: 0.5, frequency: 1, locked: false },
      { id: 'E', x: 0.5, y: 0.9, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch3-02: "Amplified Chain"
 * 5 nodes linear, 4 pathways, 1 strike.
 * Uses Amplifying to double deltas, making ring 1 act like ring 2.
 *
 * Layout: A(1)--[Amp,unison]--B(11)--[Amp,unison]--C(7)--[Clear,unison]--D(4)--[Clear,P5]--E(11)
 *
 * Strike A:
 *   B: ring1, Amp → delta=1*2=2. B: 11+2=1 (wraps). |1-1|=0. unison ✓
 *   C: ring2, Amp → delta=2*2=4. C: 7+4=11. |11-1|=10 or 2. unison=0. ✗
 * Bad. Ring 2 uses the LAST pathway, which is B-C (Amp).
 *
 * Let me try again with a simpler approach:
 *
 * Layout: A(1)--[Amp]--B(11)--[Clear]--C(11)--[Clear]--D(8)
 * All unison.
 *
 * Strike A:
 *   B: ring1, Amp → delta=2. B: 11+2=1. A-B unison ✓
 *   C: ring2, Clear → delta=2. C: 11+2=1. B-C unison: |1-1|=0 ✓
 *   D: ring3, Clear → delta=3. D: 8+3=11. C-D unison: |1-11|=10 or 2. ✗
 *
 * Need D to end at 1 too. D_start + 3 = 1 mod 12 → D_start = 10.
 *
 * Layout: A(1)--[Amp]--B(11)--[Clear]--C(11)--[Clear]--D(10)
 * All unison.
 *
 * Strike A:
 *   B: ring1, Amp → delta=2. B: 11+2=1. ✓
 *   C: ring2, Clear → delta=2. C: 11+2=1. ✓
 *   D: ring3, Clear → delta=3. D: 10+3=1. ✓
 * All freq 1. All unison ✓.
 *
 * Need 5 nodes, 5 pathways. Add node E.
 *
 * Layout: A(1)--[Amp,unison]--B(11)--[Clear,unison]--C(11)--[Clear,unison]--D(10)
 *                                                       \
 *                                                        [Damp,P5]--E(8)
 * E is ring3 from A via A→B→C→E. Last pathway C-E is Damp.
 * delta = 3 * 0.5 = 1.5 → floor = 1. E: 8+1=9. |1-9|=8 or 4. P5=7. ✗
 *
 * Hmm. Let me make E branch from B instead.
 * B-E Clear P5. E at ring2. delta=2. E_start+2 = target.
 * Need |B_final - E_final| mod12 = 7. B_final=1. E_final: (E_final-1)%12=7 → E_final=8.
 * E_start + 2 = 8. E_start=6.
 *
 * Layout:
 *   A(1)--[Amp,unison]--B(11)--[Clear,unison]--C(11)--[Clear,unison]--D(10)
 *                          \--[Clear,P5]--E(6)
 *
 * 5 nodes, 5 pathways. ✓
 *
 * Strike A:
 *   B: ring1, Amp → delta=2. B: 11+2=1. A-B unison: |1-1|=0 ✓
 *   C: ring2, Clear (B-C) → delta=2. C: 11+2=1. B-C unison: |1-1|=0 ✓
 *   E: ring2, Clear (B-E) → delta=2. E: 6+2=8. B-E P5: |1-8|=7 ✓
 *   D: ring3, Clear (C-D) → delta=3. D: 10+3=1. C-D unison: |1-1|=0 ✓
 * All satisfied! ✓
 *
 * Initial not solved:
 *   A-B: |1-11|=10 or 2 ✗
 *   B-C: |11-11|=0 ✓
 *   C-D: |11-10|=1 ✗
 *   B-E: |11-6|=5 ✗
 * Not 100% (1/5=20%) ✓
 *
 * Wait, B-C is initially satisfied. That's OK, not all need to be broken.
 * But we need initial < 100% which it is.
 */
export const ch3_02: PuzzleDefinition = {
  id: 'ch3-02',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Amplified Chain',
    category: 'cascade',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.5, frequency: 1, locked: false },
      { id: 'B', x: 0.3, y: 0.5, frequency: 11, locked: false },
      { id: 'C', x: 0.55, y: 0.5, frequency: 11, locked: false },
      { id: 'D', x: 0.8, y: 0.5, frequency: 10, locked: false },
      { id: 'E', x: 0.3, y: 0.85, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p5', source: 'A', target: 'E', type: PathwayType.Dampening, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch3-03: "Quiet Paths"
 * 5 nodes, 5 pathways, 2 strikes. Dampening-focused.
 *
 * Solved state: A=4, B=4, C=8, D=4, E=8
 *   A-B Clear unison: |4-4|=0 ✓
 *   A-C Clear M3: |4-8|=4 ✓
 *   B-D Damp unison: |4-4|=0 ✓
 *   C-E Damp M3: |8-8|=0, M3=4. ✗
 *
 * Wait, that doesn't work for M3. Let me pick:
 * Solved: A=4, B=4, C=8, D=4, E=4
 *   A-B Clear unison: ✓
 *   A-C Clear M3: |4-8|=4 ✓
 *   B-D Damp unison: ✓
 *   C-E Damp M3: |8-4|=4 ✓
 *   D-E Clear unison: |4-4|=0 ✓
 *
 * Great! Now reverse-engineer strikes.
 *
 * Topology: A(center), B(top), C(right), D(bottom-left), E(bottom-right)
 * A-B Clear, A-C Clear, B-D Damp, C-E Damp, D-E Clear
 *
 * BFS from A: ring0=A, ring1={B,C}, ring2={D (via B), E (via C)}
 * BFS from D: ring0=D, ring1={B (Damp), E (Clear)}, ring2={A (via B, Clear), C (via E, Damp)}
 *
 * Reverse strike A on solved (A=4,B=4,C=8,D=4,E=4):
 *   B: ring1, Clear → -1. B=4-1=3
 *   C: ring1, Clear → -1. C=8-1=7
 *   D: ring2, last pw=B-D Damp → delta=2*0.5=1. -1. D=4-1=3
 *   E: ring2, last pw=C-E Damp → delta=2*0.5=1. -1. E=4-1=3
 * Intermediate: A=4, B=3, C=7, D=3, E=3
 *
 * Reverse strike D on intermediate (A=4,B=3,C=7,D=3,E=3):
 *   B: ring1, Damp (D-B) → delta=floor(1*0.5)=0. B unchanged=3
 *   E: ring1, Clear (D-E) → delta=1. -1. E=3-1=2
 *   A: ring2 via D→B→A, last pw=B-A Clear → delta=2. -2. A=4-2=2
 *   C: ring2 via D→E→C, last pw=E-C... wait, there's no E-C pathway. C-E is the pathway.
 *   BFS from D: ring0=D, ring1=B(via B-D), ring1=E(via D-E).
 *   Ring2 from B: A (via A-B Clear). Ring2 from E: C (via C-E Damp, reversed).
 *   For C at ring2: last pathway is C-E Damp. delta=floor(2*0.5)=1. -1. C=7-1=6.
 * Start: A=2, B=3, C=6, D=3, E=2
 *
 * Forward verification:
 * Strike D: BFS ring0=D
 *   ring1: B via B-D(Damp) → delta=floor(1*0.5)=0. B unchanged=3
 *   ring1: E via D-E(Clear) → delta=1. E: 2+1=3
 *   ring2: A via D→B→A, last pw A-B(Clear) → delta=2. A: 2+2=4
 *   ring2: C via D→E→C, last pw C-E(Damp) → delta=floor(2*0.5)=1. C: 6+1=7
 * State: A=4, B=3, C=7, D=3, E=3
 *
 * Strike A: BFS ring0=A
 *   ring1: B via A-B(Clear) → delta=1. B: 3+1=4
 *   ring1: C via A-C(Clear) → delta=1. C: 7+1=8
 *   ring2: D via A→B→D, last pw B-D(Damp) → delta=floor(2*0.5)=1. D: 3+1=4
 *   ring2: E via A→C→E, last pw C-E(Damp) → delta=floor(2*0.5)=1. E: 3+1=4
 * State: A=4, B=4, C=8, D=4, E=4
 *
 * Check harmony:
 *   A-B Clear unison: |4-4|=0 ✓
 *   A-C Clear M3: |4-8|=4 ✓
 *   B-D Damp unison: |4-4|=0 ✓
 *   C-E Damp M3: |8-4|=4 ✓
 *   D-E Clear unison: |4-4|=0 ✓
 * All satisfied! ✓
 *
 * Check not already solved:
 *   A=2,B=3: |2-3|=1 unison ✗
 * Not 100% ✓
 */
export const ch3_03: PuzzleDefinition = {
  id: 'ch3-03',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Quiet Paths',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.5, frequency: 2, locked: false },
      { id: 'B', x: 0.5, y: 0.1, frequency: 3, locked: false },
      { id: 'C', x: 0.9, y: 0.5, frequency: 6, locked: false },
      { id: 'D', x: 0.2, y: 0.9, frequency: 3, locked: false },
      { id: 'E', x: 0.8, y: 0.9, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'C', target: 'E', type: PathwayType.Dampening, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['D', 'A'], optimal: true }],
};

/**
 * ch3-04: "Double Boost"
 * 5 nodes, 5 pathways, 1 strike. Two amplifying pathways.
 *
 * Layout: Hub A with 4 leaves B,C,D,E.
 * A-B Amp P5, A-C Amp M3, A-D Clear unison, A-E Clear m3, D-E Clear m3
 *
 * Solved: A=5, B=12, C=9, D=5, E=8
 *   A-B P5: |5-12|=7 ✓
 *   A-C M3: |5-9|=4 ✓
 *   A-D unison: |5-5|=0 ✓
 *   A-E m3: |5-8|=3 ✓
 *   D-E m3: |5-8|=3 ✓
 *
 * Strike A: All at ring1.
 *   B: Amp → delta=2. B_start=12-2=10. B: 10+2=12 ✓
 *   C: Amp → delta=2. C_start=9-2=7. C: 7+2=9 ✓
 *   D: Clear → delta=1. D_start=5-1=4. D: 4+1=5 ✓
 *   E: Clear → delta=1. E_start=8-1=7. E: 7+1=8 ✓
 *
 * Start: A=5, B=10, C=7, D=4, E=7
 *
 * Check not solved initially:
 *   A-B: |5-10|=5 P5=7 ✗
 * Not 100% ✓
 */
export const ch3_04: PuzzleDefinition = {
  id: 'ch3-04',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Double Boost',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.45, frequency: 5, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 10, locked: false },
      { id: 'C', x: 0.95, y: 0.45, frequency: 7, locked: false },
      { id: 'D', x: 0.2, y: 0.85, frequency: 4, locked: false },
      { id: 'E', x: 0.8, y: 0.85, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch3-05: "Dampened Wave"
 * 6 nodes, 6 pathways, 2 strikes.
 *
 * Topology: A--B--C--D chain, plus E branching from B, F branching from C.
 * A-B Clear, B-C Amp, C-D Clear, B-E Damp, C-F Damp
 * Plus A-E Clear
 *
 * Solved: A=6, B=6, C=6, D=6, E=6, F=6 (all unison)
 *
 * Strike A on solved, reverse:
 *   B: ring1, Clear → -1. B=5
 *   C: ring2, Amp → delta=2*2=4. -4. C=2
 *   E: ring1, ... wait, we need A-E pathway. BFS from A: ring1=B,E.
 *   ring2 from B: C(Amp), ring2 from E: nothing else.
 *   ring3 from C: D, F.
 *
 * Let me redesign topology.
 * A-B Clear, B-C Amp, C-D Clear, B-E Damp, C-F Damp, E-F Clear
 *
 * BFS from A: ring0=A, ring1=B (via A-B), ring2={C(B-C Amp), E(B-E Damp)}, ring3={D(C-D Clear), F(C-F Damp)}
 * Also ring3: F via B-E-F? ring3 from E: F(E-F Clear). So F at ring3 has 2 paths:
 *   Path 1: A→B→C→F, last pw C-F Damp
 *   Path 2: A→B→E→F, last pw E-F Clear
 *
 * Solved: all=6, all unison.
 * Reverse strike A:
 *   B: ring1, Clear → -1. B=5
 *   C: ring2, Amp → delta=2*2=4. -4. C=2
 *   E: ring2, Damp → delta=floor(2*0.5)=1. -1. E=5
 *   D: ring3, Clear → delta=3. -3. D=3
 *   F: ring3, paths: C-F Damp delta=floor(3*0.5)=1 and E-F Clear delta=3.
 *     total delta from both paths = 1+3=4. -4. F=2
 * Intermediate: A=6, B=5, C=2, D=3, E=5, F=2
 *
 * Reverse strike B on intermediate:
 * BFS from B: ring0=B, ring1={A(A-B Clear), C(B-C Amp), E(B-E Damp)}
 * ring2 from A: nothing new. Ring2 from C: D(C-D), F(C-F). Ring2 from E: F(E-F).
 * F at ring2 has 2 paths: B→C→F (last Damp) and B→E→F (last Clear).
 *   B→C→F Damp: delta=floor(2*0.5)=1
 *   B→E→F Clear: delta=2
 *   Total=3. -3. F=2-3=-1 → wrap: ((-1-1)%12+12)%12+1 = 11.
 * Hmm getting complicated. Let me simplify.
 *
 * Actually, let me try a simpler design:
 * 6 nodes, 6 pathways, 2 strikes. Linear chain with branches.
 *
 * A--[Clear]--B--[Amp]--C--[Clear]--D
 *                B--[Damp]--E
 *                C--[Damp]--F
 *
 * All unison harmonics.
 * Solved: all = 3.
 *
 * Strike C (ring0=C):
 *   ring1: B(B-C Amp) delta=1*2=2, D(C-D Clear) delta=1, F(C-F Damp) delta=floor(1*0.5)=0
 *   ring2: A(B→A, A-B Clear) delta=2, E(B→E, B-E Damp) delta=floor(2*0.5)=1
 * Reverse: B=3-2=1, D=3-1=2, F unchanged=3, A=3-2=1, E=3-1=2
 * Intermediate: A=1, B=1, C=3, D=2, E=2, F=3
 *
 * Strike A (ring0=A):
 *   ring1: B(A-B Clear) delta=1. B=1+1=2
 *   ring2: C(B-C Amp) delta=2*2=4. C=3+4=7. That's way off from 3.
 *
 * This approach is getting messy. Let me go simpler.
 *
 * ch3-05: "Dampened Wave"
 * 5 nodes, 5 pathways, 2 strikes.
 *
 * A-B Clear unison, B-C Clear P5, C-D Amp unison, C-E Damp unison
 *
 * Solved: A=3, B=3, C=10, D=10, E=10
 *   A-B: |3-3|=0 unison ✓
 *   B-C: |3-10|=7 P5 ✓
 *   C-D: |10-10|=0 unison ✓
 *   C-E: |10-10|=0 unison ✓
 *
 * BFS from B: ring0=B, ring1={A(Clear), C(Clear)}, ring2={D(Amp from C), E(Damp from C)}
 * Reverse strike B on solved:
 *   A: ring1 Clear → -1. A=2
 *   C: ring1 Clear → -1. C=9
 *   D: ring2 Amp → delta=2*2=4. -4. D=6
 *   E: ring2 Damp → delta=floor(2*0.5)=1. -1. E=9
 * Intermediate: A=2, B=3, C=9, D=6, E=9
 *
 * BFS from A: ring0=A, ring1=B(Clear). ring2=C(Clear). ring3=D(Amp), E(Damp).
 * Reverse strike A on intermediate:
 *   B: ring1 Clear → -1. B=2
 *   C: ring2 Clear → -2. C=7
 *   D: ring3 Amp → delta=3*2=6. -6. D=6-6=0 → 12.
 *   E: ring3 Damp → delta=floor(3*0.5)=1. -1. E=8
 * Start: A=2, B=2, C=7, D=12, E=8
 *
 * Forward verify:
 * Strike A:
 *   B: ring1 Clear → +1. B=2+1=3
 *   C: ring2 Clear → +2. C=7+2=9
 *   D: ring3 Amp → +6. D=12+6=18→6
 *   E: ring3 Damp → +floor(3*0.5)=1. E=8+1=9
 * State: A=2, B=3, C=9, D=6, E=9
 *
 * Strike B:
 *   A: ring1 Clear → +1. A=2+1=3
 *   C: ring1 Clear → +1. C=9+1=10
 *   D: ring2 Amp → delta=2*2=4. D=6+4=10
 *   E: ring2 Damp → delta=floor(2*0.5)=1. E=9+1=10
 * State: A=3, B=3, C=10, D=10, E=10
 *
 * Check:
 *   A-B: |3-3|=0 unison ✓
 *   B-C: |3-10|=7 P5 ✓
 *   C-D: |10-10|=0 unison ✓
 *   C-E: |10-10|=0 unison ✓
 * Need 5th pathway. Add D-E Clear unison: |10-10|=0 ✓
 *
 * Initial check (A=2,B=2,C=7,D=12,E=8):
 *   A-B: |2-2|=0 unison ✓ (already satisfied!)
 * But rest are not → not 100%. ✓
 */
export const ch3_05: PuzzleDefinition = {
  id: 'ch3-05',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Dampened Wave',
    category: 'cascade',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.3, frequency: 2, locked: false },
      { id: 'B', x: 0.3, y: 0.3, frequency: 2, locked: false },
      { id: 'C', x: 0.55, y: 0.3, frequency: 7, locked: false },
      { id: 'D', x: 0.8, y: 0.1, frequency: 12, locked: false },
      { id: 'E', x: 0.8, y: 0.55, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'C', target: 'E', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'B'], optimal: true }],
};

/**
 * ch3-06: "Power Grid"
 * 6 nodes, 6 pathways, 1 strike. Hub with 5 spokes, mixed pathway types.
 *
 * Hub A with leaves B,C,D,E,F.
 * A-B Amp P5, A-C Clear M3, A-D Damp unison, A-E Amp m3, A-F Clear unison
 * Plus one inter-leaf: D-F Damp unison
 *
 * Strike A: all leaves at ring 1.
 *   B: Amp → delta=2. Need (B_final-A) mod12 = 7 (P5). B_final = A+7 mod12.
 *     A=3. B_final=10. B_start=10-2=8.
 *   C: Clear → delta=1. (C_final-3) mod12 = 4 (M3). C_final=7. C_start=7-1=6.
 *   D: Damp → delta=floor(1*0.5)=0. D unchanged. (D-3) mod12=0. D=3. ✓
 *   E: Amp → delta=2. (E_final-3) mod12 = 3 (m3). E_final=6. E_start=6-2=4.
 *   F: Clear → delta=1. (F_final-3) mod12=0 (unison). F_final=3. F_start=3-1=2.
 *   D-F Damp: D=3, F=3. |3-3|=0. unison ✓
 *
 * Start: A=3, B=8, C=6, D=3, E=4, F=2
 *
 * Verify not initially solved:
 *   A-B: |3-8|=5 P5=7 ✗
 * Not 100% ✓
 *
 * Forward:
 * Strike A:
 *   B: ring1 Amp → +2. B=8+2=10. |3-10|=7 P5 ✓
 *   C: ring1 Clear → +1. C=6+1=7. |3-7|=4 M3 ✓
 *   D: ring1 Damp → +0. D=3. unison ✓
 *   E: ring1 Amp → +2. E=4+2=6. |3-6|=3 m3 ✓
 *   F: ring1 Clear → +1. F=2+1=3. unison ✓
 *   D-F: |3-3|=0 unison ✓
 * All 6 pathways satisfied! ✓
 */
export const ch3_06: PuzzleDefinition = {
  id: 'ch3-06',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Power Grid',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.5, frequency: 3, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 8, locked: false },
      { id: 'C', x: 0.9, y: 0.25, frequency: 6, locked: false },
      { id: 'D', x: 0.9, y: 0.75, frequency: 3, locked: false },
      { id: 'E', x: 0.1, y: 0.25, frequency: 4, locked: false },
      { id: 'F', x: 0.1, y: 0.75, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Amplifying, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'D', target: 'F', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch3-07: "Flux Balance"
 * 5 nodes, 5 pathways, 2 strikes.
 *
 * Topology: A--B--C line, with D off A and E off C.
 * A-B Amp unison, B-C Amp unison, A-D Damp P5, C-E Damp P5, D-E Clear unison
 *
 * Solved: A=6, B=6, C=6, D=1, E=1
 *   A-B: |6-6|=0 unison ✓
 *   B-C: |6-6|=0 unison ✓
 *   A-D: |6-1|=5, (1-6+12)=7. P5 ✓
 *   C-E: |6-1|=5, (1-6+12)=7. P5 ✓
 *   D-E: |1-1|=0 unison ✓
 *
 * BFS from B: ring0=B, ring1={A(A-B Amp), C(B-C Amp)}, ring2={D(A-D Damp), E(C-E Damp)}
 * Reverse strike B:
 *   A: ring1 Amp → delta=1*2=2. -2. A=4
 *   C: ring1 Amp → delta=1*2=2. -2. C=4
 *   D: ring2 Damp (last pw A-D) → delta=floor(2*0.5)=1. -1. D=12
 *   E: ring2 Damp (last pw C-E) → delta=floor(2*0.5)=1. -1. E=12
 * Intermediate: A=4, B=6, C=4, D=12, E=12
 *
 * BFS from A: ring0=A, ring1={B(A-B Amp), D(A-D Damp)}, ring2={C(B-C Amp), E(D-E Clear)}
 * Also ring2 from D: E via D-E Clear. Ring3 from C: E via C-E Damp.
 * But E already at ring2. Ring3 from B→C: E via C-E Damp → ring3. Since ring2 < ring3, E stays ring2.
 *
 * Reverse strike A on intermediate:
 *   B: ring1 Amp → delta=2. -2. B=4
 *   D: ring1 Damp → delta=floor(1*0.5)=0. D unchanged=12
 *   C: ring2 Amp (last pw B-C Amp) → delta=2*2=4. -4. C=4-4=0→12
 *   E: ring2 Clear (last pw D-E Clear) → delta=2. -2. E=12-2=10
 * Start: A=4, B=4, C=12, D=12, E=10
 *
 * Forward verify:
 * Strike A:
 *   B: ring1 Amp → +2. B=4+2=6
 *   D: ring1 Damp → +0. D=12
 *   C: ring2 Amp (B-C) → +4. C=12+4=16→4. Wait that's wrong, 16-1=15, 15%12=3, +1=4.
 *   Actually: (12+4-1)%12+1 = 15%12+1 = 3+1 = 4. C=4. But need C=4?
 *   No, solved C=6. So this is wrong.
 *
 * Hmm, the forward doesn't reproduce the solved state. The issue is that reverse-engineering
 * two strikes in sequence doesn't simply compose. Let me try different strike order.
 *
 * Forward: Strike A first, then strike B.
 *
 * Strike A on (A=4, B=4, C=12, D=12, E=10):
 *   BFS from A: ring1={B(A-B Amp), D(A-D Damp)}
 *     B: Amp delta=2. B=4+2=6
 *     D: Damp delta=0. D=12
 *   ring2 from B: C (B-C Amp). From D: E (D-E Clear).
 *     C: Amp delta=2*2=4. C=12+4=4 (wraps).
 *     E: Clear delta=2. E=10+2=12
 * State: A=4, B=6, C=4, D=12, E=12
 *
 * Strike B on (A=4, B=6, C=4, D=12, E=12):
 *   BFS from B: ring1={A(A-B Amp), C(B-C Amp)}
 *     A: Amp delta=2. A=4+2=6
 *     C: Amp delta=2. C=4+2=6
 *   ring2 from A: D(A-D Damp). From C: E(C-E Damp).
 *     D: Damp delta=floor(2*0.5)=1. D=12+1=1
 *     E: Damp delta=floor(2*0.5)=1. E=12+1=1
 * State: A=6, B=6, C=6, D=1, E=1
 *
 * Check harmony:
 *   A-B: |6-6|=0 unison ✓
 *   B-C: |6-6|=0 unison ✓
 *   A-D: (6-1)=5, (1-6+12)=7. P5 ✓
 *   C-E: (6-1)=5, (1-6+12)=7. P5 ✓
 *   D-E: |1-1|=0 unison ✓
 * All satisfied! ✓
 *
 * Check not initially solved:
 *   A-B: |4-4|=0 unison ✓ (satisfied)
 *   B-C: |4-12|=8 or 4. unison=0. ✗
 * Not 100% ✓
 */
export const ch3_07: PuzzleDefinition = {
  id: 'ch3-07',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Flux Balance',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.25, y: 0.5, frequency: 4, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 4, locked: false },
      { id: 'C', x: 0.75, y: 0.5, frequency: 12, locked: false },
      { id: 'D', x: 0.15, y: 0.85, frequency: 12, locked: false },
      { id: 'E', x: 0.85, y: 0.85, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Dampening, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'C', target: 'E', type: PathwayType.Dampening, harmonic: 'P5', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'B'], optimal: true }],
};

/**
 * ch3-08: "Muted Cascade"
 * 6 nodes, 6 pathways, 1 strike. Tree topology.
 *
 * A(hub)--B(ring1 Clear)--D(ring2 Damp), B--E(ring2 Amp)
 * A--C(ring1 Amp)--F(ring2 Clear)
 * Plus D-F Clear
 *
 * Solved: A=2, B=2, C=2, D=2, E=2, F=2 (all unison)
 * But that's boring. Let me use mixed intervals.
 *
 * Solved: A=1, B=8, C=5, D=8, E=12, F=12
 *   A-B Clear P5: |1-8|=7 ✓
 *   A-C Amp M3: |1-5|=4 ✓
 *   B-D Damp unison: |8-8|=0 ✓
 *   B-E Amp M3: |8-12|=4 ✓
 *   C-F Clear P5: |5-12|=7 ✓
 *   D-F Clear unison: |8-12|=4. unison=0. ✗
 *
 * Hmm. Let me fix D-F. |D-F| = 0 for unison. D=F. If D=8, F=8.
 * Then C-F P5: |5-8|=3. P5=7. ✗.
 * C-F unison: |5-8|=3 ✗.
 *
 * Let me pick different intervals.
 * Solved: A=1, B=8, C=5, D=8, E=12, F=8
 *   A-B Clear P5: |1-8|=7 ✓
 *   A-C Amp M3: |1-5|=4 ✓
 *   B-D Damp unison: |8-8|=0 ✓
 *   B-E Amp M3: |8-12|=4 ✓
 *   C-F Amp m3: |5-8|=3 ✓
 *   D-F Clear unison: |8-8|=0 ✓
 *
 *
 * Strike A:
 *   B: ring1, Clear → delta=1. B_start=8-1=7
 *   C: ring1, Amp → delta=2. C_start=5-2=3
 *   D: ring2, last pw B-D Damp → delta=floor(2*0.5)=1. D_start=8-1=7
 *   E: ring2, last pw B-E Amp → delta=2*2=4. E_start=12-4=8
 *   F: ring2, last pw C-F Amp → delta=2*2=4. F_start=8-4=4
 *
 * Start: A=1, B=7, C=3, D=7, E=8, F=4
 *
 * Check not initially solved:
 *   A-B: |1-7|=6 P5=7 ✗
 * Not 100% ✓
 *
 * Forward:
 * Strike A:
 *   B: ring1, Clear → +1. B=7+1=8. A-B: |1-8|=7 P5 ✓
 *   C: ring1, Amp → +2. C=3+2=5. A-C: |1-5|=4 M3 ✓
 *   D: ring2, Damp → +1. D=7+1=8. B-D: |8-8|=0 unison ✓
 *   E: ring2, Amp → +4. E=8+4=12. B-E: |8-12|=4 M3 ✓
 *   F: ring2, Amp → +4. F=4+4=8. C-F: |5-8|=3 m3 ✓
 *   D-F: |8-8|=0 unison ✓
 * All satisfied! ✓
 */
export const ch3_08: PuzzleDefinition = {
  id: 'ch3-08',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Muted Cascade',
    category: 'cascade',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.15, frequency: 1, locked: false },
      { id: 'B', x: 0.25, y: 0.45, frequency: 7, locked: false },
      { id: 'C', x: 0.75, y: 0.45, frequency: 3, locked: false },
      { id: 'D', x: 0.1, y: 0.8, frequency: 7, locked: false },
      { id: 'E', x: 0.4, y: 0.8, frequency: 8, locked: false },
      { id: 'F', x: 0.9, y: 0.8, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'C', target: 'F', type: PathwayType.Amplifying, harmonic: 'm3', directed: false },
      { id: 'p6', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch3-09: "Frequency Split"
 * 6 nodes, 6 pathways, 2 strikes.
 *
 * Topology: A-B Clear, A-C Amp, B-D Amp, C-E Clear, D-E Clear, B-E Damp
 *
 * Solved: A=5, B=5, C=9, D=5, E=12
 *   A-B Clear unison: |5-5|=0 ✓
 *   A-C Amp M3: |5-9|=4 ✓
 *   B-D Amp unison: |5-5|=0 ✓
 *   C-E Clear m3: |9-12|=3 ✓
 *   D-E Clear P5: |5-12|=7 ✓
 *   B-E Damp P5: |5-12|=7 ✓
 *
 * BFS from A: ring0=A, ring1={B(A-B Clear), C(A-C Amp)}
 * ring2 from B: D(B-D Amp), E(B-E Damp). From C: E(C-E Clear).
 * E at ring2 has 2 paths: A→B→E(Damp) and A→C→E(Clear).
 *   Damp: delta=floor(2*0.5)=1. Clear: delta=2. Total=3.
 * ring3 from D: E already visited. Nothing new.
 *
 * Reverse strike A:
 *   B: ring1, Clear → -1. B=4
 *   C: ring1, Amp → -2. C=7
 *   D: ring2, Amp → -4. D=1
 *   E: ring2, total=3 → -3. E=9
 * Intermediate: A=5, B=4, C=7, D=1, E=9
 *
 * BFS from C: ring0=C, ring1={A(A-C Amp), E(C-E Clear)}
 * ring2 from A: B(A-B Clear). From E: B(B-E Damp), D(D-E Clear).
 * B at ring2 has 2 paths: C→A→B(Clear) and C→E→B(Damp).
 *   Clear: delta=2. Damp: delta=floor(2*0.5)=1. Total=3.
 * D at ring2: path C→E→D, last pw D-E Clear. delta=2.
 * ring3 from B: D(B-D Amp). D already at ring2 via E. ring2 < ring3, so D stays ring2.
 *
 * Reverse strike C on intermediate:
 *   A: ring1, Amp → -2. A=3
 *   E: ring1, Clear → -1. E=8
 *   B: ring2, total=3 → -3. B=1
 *   D: ring2, Clear → -2. D=11
 * Start: A=3, B=1, C=7, D=11, E=8
 *
 * Forward:
 * Strike C on (A=3, B=1, C=7, D=11, E=8):
 *   A: ring1 Amp → +2. A=3+2=5
 *   E: ring1 Clear → +1. E=8+1=9
 *   B: ring2 total=3. B=1+3=4
 *   D: ring2 Clear → +2. D=11+2=1
 * State: A=5, B=4, C=7, D=1, E=9
 *
 * Strike A on (A=5, B=4, C=7, D=1, E=9):
 *   B: ring1 Clear → +1. B=4+1=5
 *   C: ring1 Amp → +2. C=7+2=9
 *   D: ring2 Amp → +4. D=1+4=5
 *   E: ring2 total=3. E=9+3=12
 * State: A=5, B=5, C=9, D=5, E=12
 *
 * Check:
 *   A-B: |5-5|=0 unison ✓
 *   A-C: |5-9|=4 M3 ✓
 *   B-D: |5-5|=0 unison ✓
 *   C-E: |9-12|=3 m3 ✓
 *   D-E: |5-12|=7 P5 ✓
 *   B-E: |5-12|=7 P5 ✓
 * All satisfied! ✓
 *
 * Initially (A=3, B=1, C=7, D=11, E=8):
 *   A-B: |3-1|=2 unison ✗
 * Not 100% ✓
 */
export const ch3_09: PuzzleDefinition = {
  id: 'ch3-09',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Frequency Split',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.3, y: 0.2, frequency: 3, locked: false },
      { id: 'B', x: 0.1, y: 0.6, frequency: 1, locked: false },
      { id: 'C', x: 0.7, y: 0.2, frequency: 7, locked: false },
      { id: 'D', x: 0.3, y: 0.9, frequency: 11, locked: false },
      { id: 'E', x: 0.7, y: 0.7, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'D', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'C', target: 'E', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'B', target: 'E', type: PathwayType.Dampening, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['C', 'A'], optimal: true }],
};

/**
 * ch3-10: "Resonant Matrix"
 * 7 nodes, 7 pathways, 2 strikes. Chapter capstone.
 *
 * Topology: Central hub A with B,C,D around it, E off B, F off C, G off D.
 * A-B Clear, A-C Amp, A-D Damp, B-E Amp, C-F Clear, D-G Clear
 * Plus E-F Damp
 *
 * Solved: A=1, B=8, C=5, D=1, E=12, F=12, G=1
 *   A-B Clear P5: |1-8|=7 ✓
 *   A-C Amp M3: |1-5|=4 ✓
 *   A-D Damp unison: |1-1|=0 ✓
 *   B-E Amp M3: |8-12|=4 ✓
 *   C-F Clear P5: |5-12|=7 ✓
 *   D-G Clear unison: |1-1|=0 ✓
 *   E-F Damp unison: |12-12|=0 ✓
 *
 * BFS from A: ring0=A, ring1={B(Clear), C(Amp), D(Damp)},
 * ring2={E(B-E Amp), F(C-F Clear), G(D-G Clear)}
 * ring3: E-F → ring3 for whichever isn't already ring2. Both at ring2, so no.
 *
 * Reverse strike A:
 *   B: ring1, Clear → -1. B=7
 *   C: ring1, Amp → -2. C=3
 *   D: ring1, Damp → -0. D=1
 *   E: ring2, Amp → -4. E=8
 *   F: ring2, Clear → -2. F=10
 *   G: ring2, Clear → -2. G=11
 * Intermediate: A=1, B=7, C=3, D=1, E=8, F=10, G=11
 *
 * BFS from B: ring0=B, ring1={A(A-B Clear), E(B-E Amp)}
 * ring2 from A: C(A-C Amp), D(A-D Damp). From E: F(E-F Damp).
 * ring3 from C: F(C-F Clear). F already at ring2, stays ring2.
 * ring3 from D: G(D-G Clear).
 *
 * Reverse strike B on intermediate:
 *   A: ring1, Clear → -1. A=12
 *   E: ring1, Amp → -2. E=6
 *   C: ring2, Amp (A-C) → -4. C=3-4=-1→11
 *   D: ring2, Damp (A-D) → -floor(2*0.5)=-1. D=12
 *   F: ring2, Damp (E-F) → -floor(2*0.5)=-1. F=9
 *   G: ring3, Clear (D-G) → -3. G=11-3=8
 * Start: A=12, B=7, C=11, D=12, E=6, F=9, G=8
 *
 * Forward:
 * Strike B on (A=12, B=7, C=11, D=12, E=6, F=9, G=8):
 *   A: ring1 Clear → +1. A=12+1=1
 *   E: ring1 Amp → +2. E=6+2=8
 *   C: ring2 Amp → +4. C=11+4=3
 *   D: ring2 Damp → +1. D=12+1=1
 *   F: ring2 Damp (E-F) → +1. F=9+1=10
 *   G: ring3 Clear → +3. G=8+3=11
 * State: A=1, B=7, C=3, D=1, E=8, F=10, G=11
 *
 * Strike A on (A=1, B=7, C=3, D=1, E=8, F=10, G=11):
 *   B: ring1 Clear → +1. B=8
 *   C: ring1 Amp → +2. C=5
 *   D: ring1 Damp → +0. D=1
 *   E: ring2 Amp → +4. E=12
 *   F: ring2 Clear → +2. F=12
 *   G: ring2 Clear → +2. G=1 (11+2=13→1)
 * State: A=1, B=8, C=5, D=1, E=12, F=12, G=1
 *
 * Check:
 *   A-B P5: |1-8|=7 ✓
 *   A-C M3: |1-5|=4 ✓
 *   A-D unison: |1-1|=0 ✓
 *   B-E M3: |8-12|=4 ✓
 *   C-F P5: |5-12|=7 ✓
 *   D-G unison: |1-1|=0 ✓
 *   E-F unison: |12-12|=0 ✓
 * All satisfied! ✓
 *
 * Initially (A=12, B=7, C=11, D=12, E=6, F=9, G=8):
 *   A-B: |12-7|=5, (7-12+12)=7. P5 ✓ (one already satisfied)
 *   A-C: |12-11|=1 M3=4 ✗
 * Not 100% ✓
 */
export const ch3_10: PuzzleDefinition = {
  id: 'ch3-10',
  version: 1,
  tier: 3,
  metadata: {
    title: 'Resonant Matrix',
    category: 'composition',
    author: 'resonance-team',
    created: '2026-02-01',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.4, frequency: 12, locked: false },
      { id: 'B', x: 0.2, y: 0.15, frequency: 7, locked: false },
      { id: 'C', x: 0.8, y: 0.15, frequency: 11, locked: false },
      { id: 'D', x: 0.5, y: 0.7, frequency: 12, locked: false },
      { id: 'E', x: 0.05, y: 0.5, frequency: 6, locked: false },
      { id: 'F', x: 0.95, y: 0.5, frequency: 9, locked: false },
      { id: 'G', x: 0.5, y: 0.95, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'D', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'E', target: 'F', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 4,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['B', 'A'], optimal: true }],
};

export const chapter3Puzzles: PuzzleDefinition[] = [
  ch3_01, ch3_02, ch3_03, ch3_04, ch3_05,
  ch3_06, ch3_07, ch3_08, ch3_09, ch3_10,
];
